/**
 * DOM panels and dialogs: loading screen, status bar, move history, and the
 * New Game / Game Over / How to Play / About dialogs. All DOM access lives
 * here and in main.ts; the renderer owns only the canvas.
 */

import type { GameMode, MoveLogEntry, Strength } from '../protocol/messages';

export interface NewGameOptions {
  mode: GameMode;
  humanFirst: boolean;
  strength: Strength;
}

export interface AboutInfo {
  engineVersion: string;
  pyodideVersion: string;
  sab: boolean;
}

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`missing element #${id}`);
  return found as T;
}

export class Panels {
  private loading = el<HTMLDivElement>('loading');
  private loadingStage = el<HTMLDivElement>('loading-stage');
  private loadingDetail = el<HTMLDivElement>('loading-detail');
  private loadingSpinner = el<HTMLDivElement>('loading-spinner');
  private app = el<HTMLDivElement>('app');
  private turnLabel = el<HTMLDivElement>('turn-label');
  private aiInfo = el<HTMLDivElement>('ai-info');
  private modeLabel = el<HTMLSpanElement>('mode-label');
  private history = el<HTMLOListElement>('history');
  private undoButton = el<HTMLButtonElement>('btn-undo');
  private muteButton = el<HTMLButtonElement>('btn-mute');
  private flipButton = el<HTMLButtonElement>('btn-flip');
  private strengthSelect = el<HTMLSelectElement>('sel-strength');
  private newGameDialog = el<HTMLDialogElement>('dlg-new');
  private gameOverDialog = el<HTMLDialogElement>('dlg-over');
  private howToDialog = el<HTMLDialogElement>('dlg-howto');
  private aboutDialog = el<HTMLDialogElement>('dlg-about');

  // -- loading screen ---------------------------------------------------------

  setLoading(stage: string, detail?: string): void {
    this.loadingStage.textContent = stage;
    this.loadingDetail.textContent = detail ?? '';
  }

  failLoading(message: string): void {
    this.loadingSpinner.classList.add('failed');
    this.setLoading('Failed to start the game engine', message);
  }

  hideLoading(): void {
    this.loading.hidden = true;
    this.app.hidden = false;
  }

  // -- status area --------------------------------------------------------------

  setTurn(text: string): void {
    this.turnLabel.textContent = text;
  }

  setAiInfo(text: string): void {
    this.aiInfo.textContent = text;
  }

  setMode(text: string): void {
    this.modeLabel.textContent = text;
  }

  setHistory(entries: MoveLogEntry[]): void {
    this.history.replaceChildren(
      ...entries.map((entry) => {
        const li = document.createElement('li');
        li.textContent = entry.notation;
        li.value = entry.ply;
        if (entry.capture) li.classList.add('capture');
        li.classList.add(entry.side === 'RED' ? 'side-red' : 'side-blue');
        return li;
      }),
    );
    this.history.scrollTop = this.history.scrollHeight;
  }

  setUndoEnabled(enabled: boolean): void {
    this.undoButton.disabled = !enabled;
  }

  setMuted(muted: boolean): void {
    this.muteButton.textContent = muted ? '🔇' : '🔊';
    this.muteButton.title = muted ? 'Unmute sounds' : 'Mute sounds';
    this.muteButton.setAttribute('aria-pressed', String(muted));
  }

  setFlipped(flipped: boolean): void {
    this.flipButton.setAttribute('aria-pressed', String(flipped));
  }

  setStrength(strength: Strength): void {
    this.strengthSelect.value = strength;
  }

  // -- dialogs --------------------------------------------------------------------

  /** Returns the chosen options, or null when cancelled. */
  openNewGame(current: NewGameOptions, allowCancel: boolean): Promise<NewGameOptions | null> {
    const dialog = this.newGameDialog;
    const mode = el<HTMLSelectElement>('new-mode');
    const first = el<HTMLSelectElement>('new-first');
    const strength = el<HTMLSelectElement>('new-strength');
    const cancel = el<HTMLButtonElement>('new-cancel');
    mode.value = current.mode;
    first.value = current.humanFirst ? 'human' : 'ai';
    strength.value = current.strength;
    cancel.hidden = !allowCancel;
    const syncFirst = () => {
      first.disabled = mode.value !== 'human_vs_ai';
    };
    mode.addEventListener('change', syncFirst);
    syncFirst();

    return new Promise((resolve) => {
      const cleanup = () => {
        dialog.removeEventListener('close', onClose);
        mode.removeEventListener('change', syncFirst);
      };
      const onClose = () => {
        cleanup();
        if (dialog.returnValue === 'ok') {
          resolve({
            mode: mode.value as GameMode,
            humanFirst: first.value === 'human',
            strength: strength.value as Strength,
          });
        } else {
          resolve(null);
        }
      };
      dialog.addEventListener('close', onClose);
      dialog.showModal();
    });
  }

  /** Non-blocking result notice; resolves on New Game ('new') or Close ('close'). */
  openGameOver(message: string): Promise<'new' | 'close'> {
    const dialog = this.gameOverDialog;
    el<HTMLParagraphElement>('over-message').textContent = message;
    return new Promise((resolve) => {
      const onClose = () => {
        dialog.removeEventListener('close', onClose);
        resolve(dialog.returnValue === 'new' ? 'new' : 'close');
      };
      dialog.addEventListener('close', onClose);
      dialog.showModal();
    });
  }

  closeGameOver(): void {
    if (this.gameOverDialog.open) this.gameOverDialog.close('close');
  }

  openHowTo(): void {
    this.howToDialog.showModal();
  }

  openAbout(info: AboutInfo): void {
    el<HTMLSpanElement>('about-engine').textContent = info.engineVersion;
    el<HTMLSpanElement>('about-pyodide').textContent = info.pyodideVersion;
    el<HTMLSpanElement>('about-sab').textContent = info.sab ? 'on' : 'off';
    this.aboutDialog.showModal();
  }
}
